import * as THREE from 'three';
import { ATTRACTION_LADDER } from '../config/catalog';
import {
  createQueueSlots,
  queueLaneFrame,
  type QueueLayout,
  type QueueSlot,
} from '../spikes/threejs/queue';
import type { EntityKind, RotationQuarter } from '../types';
import type { EntityRenderSpec, MigrationRenderModel } from './mapParkState';

export const QUEUE_CAPACITY = 5;
export const QUEUE_CYCLE_SECONDS = 3.2;
export const QUEUE_ATTRACTION_KINDS = ATTRACTION_LADDER;
export const QUEUE_KIOSK_KINDS: readonly EntityKind[] = ['snack_kiosk', 'drink_kiosk'];
export const KIOSK_QUEUE_CAPACITY = 3;

/**
 * Slot-local boarding: path-facing and camera-forward (+Z), not a long run
 * through the inner neighbor or the central path. Approved lane width stays 0.72.
 */
type QueuePlacement = {
  /** X offset of the boarding end. Positive is toward the central path. */
  boardingTowardPath: number;
  /** +Z offset of the boarding end (camera-forward / entrance side). */
  boardingSouth: number;
  /** Extra X from end to start. Positive is toward the path. */
  laneTowardPath: number;
  /** Extra +Z from end to start. */
  laneSouth: number;
};

type QueueKind =
  | 'starter_ride'
  | 'spinning_ride'
  | 'pirate_ship'
  | 'ferris_wheel'
  | 'roller_coaster'
  | 'snack_kiosk'
  | 'drink_kiosk';

const QUEUE_PLACEMENT: Record<QueueKind, QueuePlacement> = {
  starter_ride: {
    boardingTowardPath: 1.02,
    boardingSouth: 0.92,
    laneTowardPath: 0.42,
    laneSouth: 1.18,
  },
  spinning_ride: {
    boardingTowardPath: 0.96,
    boardingSouth: 0.88,
    laneTowardPath: 0.38,
    laneSouth: 1.2,
  },
  pirate_ship: {
    boardingTowardPath: 0.38,
    boardingSouth: 1.08,
    laneTowardPath: 0.22,
    laneSouth: 1.22,
  },
  ferris_wheel: {
    boardingTowardPath: -0.12,
    boardingSouth: 0.78,
    laneTowardPath: -0.18,
    laneSouth: 1.24,
  },
  roller_coaster: {
    boardingTowardPath: -0.52,
    boardingSouth: 0.92,
    laneTowardPath: -0.12,
    laneSouth: 1.22,
  },
  snack_kiosk: {
    boardingTowardPath: 0.12,
    boardingSouth: 0.82,
    laneTowardPath: 0.16,
    laneSouth: 0.7,
  },
  drink_kiosk: {
    boardingTowardPath: 0.1,
    boardingSouth: 0.78,
    laneTowardPath: 0.14,
    laneSouth: 0.68,
  },
};

const DEFAULT_PLACEMENT = QUEUE_PLACEMENT.starter_ride;
export const QUEUE_LANE_WIDTH = 0.72;
/** Central path rect is 1.65 wide; keep the lane centerline outside rails+shuffle. */
export const QUEUE_PATH_CLEAR_X = 1.36;

function keepOffPath(x: number): number {
  if (Math.abs(x) >= QUEUE_PATH_CLEAR_X) {
    return x;
  }
  return (x < 0 ? -1 : 1) * QUEUE_PATH_CLEAR_X;
}

export type QueueLane = {
  entityId: string;
  kind: EntityKind;
  slotId: string;
  attractionX: number;
  attractionZ: number;
  layout: QueueLayout;
  capacity: number;
  occupancy: number;
};

export type QueueGuestPose = QueueSlot & {
  logicalIndex: number;
  boarding: boolean;
};

export type QueueLaneEvidence = {
  entityId: string;
  kind: EntityKind;
  slotId: string;
  occupancy: number;
  capacity: number;
  cycle: number;
  visibleGuests: number;
  front: { x: number; z: number } | null;
};

export type QueueEvidence = {
  elapsed: number;
  queues: QueueLaneEvidence[];
  queueCount: number;
  guestCount: number;
  visibleGuestCount: number;
};

export function attractionHasQueue(kind: EntityKind): boolean {
  return (QUEUE_ATTRACTION_KINDS as readonly EntityKind[]).includes(kind);
}

export function kioskHasQueue(kind: EntityKind): boolean {
  return QUEUE_KIOSK_KINDS.includes(kind);
}

export function entityHasQueue(kind: EntityKind): boolean {
  return attractionHasQueue(kind) || kioskHasQueue(kind);
}

export function queueCapacityForKind(kind: EntityKind): number {
  return kioskHasQueue(kind) ? KIOSK_QUEUE_CAPACITY : QUEUE_CAPACITY;
}

export function queueSignLabel(kind: EntityKind): string {
  if (kind === 'snack_kiosk') {
    return 'FOOD';
  }
  if (kind === 'drink_kiosk') {
    return 'DRINK';
  }
  return 'RIDE';
}

/** Match Three.js Y-up yaw so a 90° entity rotation also turns the queue. */
export function rotatePointAroundY(
  x: number,
  z: number,
  originX: number,
  originZ: number,
  rotation: RotationQuarter,
): { x: number; z: number } {
  if (rotation === 0) {
    return { x, z };
  }
  const rad = THREE.MathUtils.degToRad(rotation);
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = x - originX;
  const dz = z - originZ;
  return {
    x: originX + dx * cos + dz * sin,
    z: originZ - dx * sin + dz * cos,
  };
}

export function seedFromId(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  }
  return (hash % 1000) / 1000;
}

export function queueCycle(elapsed: number, seed = 0): number {
  const cycles = elapsed / QUEUE_CYCLE_SECONDS + seed;
  return cycles - Math.floor(cycles);
}

export function queueLayoutForAttraction(
  spec: Pick<EntityRenderSpec, 'kind' | 'x' | 'z'> & { rotation?: RotationQuarter },
): QueueLayout {
  const towardPath = spec.x < 0 ? 1 : -1;
  const placement = QUEUE_PLACEMENT[spec.kind as QueueKind] ?? DEFAULT_PLACEMENT;
  const unrotatedEnd = new THREE.Vector2(
    keepOffPath(spec.x + towardPath * placement.boardingTowardPath),
    spec.z + placement.boardingSouth,
  );
  const unrotatedStart = new THREE.Vector2(
    keepOffPath(unrotatedEnd.x + towardPath * placement.laneTowardPath),
    unrotatedEnd.y + placement.laneSouth,
  );
  const rotation = spec.rotation ?? 0;
  const endPt = rotatePointAroundY(unrotatedEnd.x, unrotatedEnd.y, spec.x, spec.z, rotation);
  const startPt = rotatePointAroundY(unrotatedStart.x, unrotatedStart.y, spec.x, spec.z, rotation);
  return {
    start: new THREE.Vector2(startPt.x, startPt.z),
    end: new THREE.Vector2(endPt.x, endPt.z),
    width: QUEUE_LANE_WIDTH,
  };
}

export function queueLanesFromModel(model: MigrationRenderModel): QueueLane[] {
  return model.entities.filter((entity) => entityHasQueue(entity.kind)).map((entity) => {
    const capacity = queueCapacityForKind(entity.kind);
    return {
      entityId: entity.id,
      kind: entity.kind,
      slotId: entity.slotId,
      attractionX: entity.x,
      attractionZ: entity.z,
      layout: queueLayoutForAttraction(entity),
      capacity,
      occupancy: capacity,
    };
  });
}

export function restSlotsForLane(lane: QueueLane): QueueSlot[] {
  return createQueueSlots(lane.layout, lane.capacity);
}

function smoothstep(t: number): number {
  const clamped = Math.min(1, Math.max(0, t));
  return clamped * clamped * (3 - 2 * clamped);
}

export function queueGuestPoses(lane: QueueLane, elapsed: number): QueueGuestPose[] {
  const rest = restSlotsForLane(lane);
  const seed = seedFromId(lane.entityId);
  const cycles = elapsed / QUEUE_CYCLE_SECONDS + seed;
  const whole = Math.floor(cycles);
  const frac = cycles - whole;
  const ease = smoothstep(frac);
  const { dir } = queueLaneFrame(lane.layout);
  const board = lane.layout.end.clone().addScaledVector(dir, 0.32);
  const back = rest[lane.capacity - 1]!;
  const behind = {
    x: back.x - dir.x * 0.34,
    z: back.z - dir.y * 0.34,
  };

  const poses: QueueGuestPose[] = [];
  for (let instance = 0; instance < lane.occupancy; instance += 1) {
    const fromIndex = ((instance - whole) % lane.capacity + lane.capacity) % lane.capacity;
    const from = rest[fromIndex]!;
    if (fromIndex === 0) {
      if (frac < 0.84) {
        const travel = smoothstep(frac / 0.84);
        poses.push({
          x: THREE.MathUtils.lerp(from.x, board.x, travel),
          z: THREE.MathUtils.lerp(from.z, board.y, travel),
          facing: from.facing,
          phase: from.phase,
          hidden: frac >= 0.8,
          logicalIndex: fromIndex,
          boarding: true,
        });
      } else {
        const arrive = (frac - 0.84) / 0.16;
        poses.push({
          x: THREE.MathUtils.lerp(behind.x, back.x, arrive),
          z: THREE.MathUtils.lerp(behind.z, back.z, arrive),
          facing: back.facing,
          phase: back.phase,
          hidden: false,
          logicalIndex: lane.capacity - 1,
          boarding: false,
        });
      }
      continue;
    }
    const to = rest[fromIndex - 1]!;
    poses.push({
      x: THREE.MathUtils.lerp(from.x, to.x, ease),
      z: THREE.MathUtils.lerp(from.z, to.z, ease),
      facing: THREE.MathUtils.lerp(from.facing, to.facing, ease),
      phase: from.phase,
      hidden: false,
      logicalIndex: fromIndex,
      boarding: false,
    });
  }
  return poses;
}

export function applyQueuePoses(slots: QueueSlot[], poses: QueueGuestPose[]): void {
  for (let i = 0; i < slots.length; i += 1) {
    const pose = poses[i];
    const slot = slots[i];
    if (!pose || !slot) {
      continue;
    }
    slot.x = pose.x;
    slot.z = pose.z;
    slot.facing = pose.facing;
    slot.phase = pose.phase;
    slot.hidden = pose.hidden;
  }
}

export function queueEvidenceFromLanes(lanes: QueueLane[], elapsed: number): QueueEvidence {
  const queues = lanes.map((lane) => {
    const poses = queueGuestPoses(lane, elapsed);
    const visible = poses.filter((pose) => !pose.hidden);
    const front = visible.find((pose) => pose.logicalIndex === 0) ?? visible[0] ?? null;
    return {
      entityId: lane.entityId,
      kind: lane.kind,
      slotId: lane.slotId,
      occupancy: lane.occupancy,
      capacity: lane.capacity,
      cycle: queueCycle(elapsed, seedFromId(lane.entityId)),
      visibleGuests: visible.length,
      front: front ? { x: front.x, z: front.z } : null,
    };
  });
  return {
    elapsed,
    queues,
    queueCount: queues.length,
    guestCount: queues.reduce((sum, queue) => sum + queue.occupancy, 0),
    visibleGuestCount: queues.reduce((sum, queue) => sum + queue.visibleGuests, 0),
  };
}
